import React from 'react';
import Typography from 'apollo-react/components/Typography';
import Box from 'apollo-react/components/Box';
import Button from 'apollo-react/components/Button';
import Download from 'apollo-react-icons/Download';
import Eye from 'apollo-react-icons/EyeShow';
import FileText from 'apollo-react-icons/File';
import { useFileDownload, useFilePreview } from '../../hooks';
import DownloadProgress from './DownloadProgress';
import { toast } from 'react-toastify';

/**
 * View-only file display component for forms
 * Shows download and preview buttons for uploaded files
 * Preview button appears only for previewable file types (images, PDFs, Office docs)
 */
const ViewOnlyFileDisplay = ({ 
  files = [],
  label, 
  helperText,
  persona = 'customer',
  ...rest 
}) => {
  const { downloadFile, downloading } = useFileDownload(persona);
  const { canPreviewFile } = useFilePreview(persona);

  const handleDownload = async (fileId, fileName) => {
    await downloadFile(fileId, fileName);
  };

  /**
   * Open file preview in a new browser tab using a more reliable method
   * that bypasses popup blockers by simulating a direct link click
   * @param {number|string} fileId - The file ID to preview
   * @param {string} fileName - The file name for error messages
   */
  const handlePreview = (fileId, fileName) => {
    try {
      // Determine endpoint based on persona
      const endpoint = persona === 'customer' 
        ? `/api/files/preview/${fileId}`
        : `/api/files/admin/preview/${fileId}`;
      
      // Create the full URL
      const fullUrl = `${process.env.REACT_APP_SERVER_URL}${endpoint}`;
      
      // Create a temporary anchor element and click it programmatically
      // This method is less likely to be blocked by popup blockers
      const link = document.createElement('a');
      link.href = fullUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // Add to DOM temporarily, click it, then remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error opening preview:', error);
      toast.error(`Failed to open preview for ${fileName}`, {
        position: "top-center"
      });
    }
  };

  // Normalize files to array
  const filesArray = Array.isArray(files) ? files : [files].filter(Boolean);

  return (
    <Box sx={{ marginBottom: 2 }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          fontWeight: 500, 
          marginBottom: 1,
          color: '#333'
        }}
      >
        {label}
      </Typography>
      
      {filesArray.length > 0 ? (
        <Box sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          padding: 2,
          backgroundColor: '#f8f9fa'
        }}>
          {filesArray.map((file, index) => {
            const fileId = file.id || file.file_id;
            const fileName = file.name || file.file_name || file.original_name || `File ${index + 1}`;
            const mimeType = file.mime_type || file.type || '';
            const isPreviewable = canPreviewFile(mimeType);
            
            return (
              <Box key={index}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    padding: 1,
                    marginBottom: 0,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    border: '1px solid #ddd'
                  }}
                >
                  <FileText size={16} color="#666" />
                  <Typography variant="body2" sx={{ flex: 1, textOverflow: "ellipsis", overflow: "hidden" }}>
                    {fileName}
                    {mimeType && (
                      <Typography component="span" variant="caption" sx={{ display: 'block', color: '#888' }}>
                        {mimeType}
                      </Typography>
                    )}
                  </Typography>
                  
                  {/* Action buttons container */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* Preview button - only show for previewable files */}
                    {isPreviewable && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Eye size={14} />}
                        onClick={() => handlePreview(fileId, fileName)}
                        sx={{ 
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: '#e3f2fd',
                            borderColor: '#1976d2'
                          }
                        }}
                      >
                        Preview
                      </Button>
                    )}
                    
                    {/* Download button */}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Download size={14} />}
                      onClick={() => handleDownload(fileId, fileName)}
                      disabled={downloading}
                      sx={{ minWidth: 'auto' }}
                    >
                      {downloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </Box>
                </Box>
                
                {/* Show download progress for this specific file */}
                <DownloadProgress 
                  fileId={`download_${fileId}`} 
                  fileName={fileName}
                />
                
                {/* Add margin between files except for the last one */}
                {index < filesArray.length - 1 && <Box sx={{ marginBottom: 1 }} />}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          padding: 2,
          backgroundColor: '#f8f9fa',
          textAlign: 'center'
        }}>
          <Typography variant="body2" color="text.secondary">
            No file uploaded
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ViewOnlyFileDisplay;
